import type { LayerFeature, SyncMode } from 'components/ImageViewer/types';
import type { PlaybackLayer } from 'components/Playback/types';
import {
  normalizeImageIndex,
  getFeatureSyncValue,
} from 'utils/syncIndex';
// @ts-expect-error
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { projectGeometry } from 'esri/projectGeometry';

export {
  normalizeImageIndex,
  getFeatureSyncValue,
  buildPlaybackTimelineSyncValues,
  resolvePlaybackStartPosition,
} from 'utils/syncIndex';

export {
  SURVEY_IMAGE_DRAG_MIME,
  MAX_DROP_TO_SURVEY_IMAGE_DISTANCE_METERS,
  resolveMaxDropDistanceMeters,
} from './surveyImageDrag';

interface PointLike {
  readonly x?: number;
  readonly y?: number;
  readonly spatialReference?: { readonly wkid?: number };
}

export type PlaybackPinPoint = {
  readonly x: number;
  readonly y: number;
  readonly spatialReference?: { readonly wkid?: number };
};

interface FeatureGeometryLike extends PointLike {
  readonly type?: string;
  readonly extent?: {
    readonly center?: PointLike;
  };
}

export function isWfsImageLayer(layer: {
  readonly type: string;
  readonly endpoint: string;
}): boolean {
  const urlLower = (layer.endpoint || '').toLowerCase();
  return (
    layer.type === 'WFS' ||
    urlLower.includes('service=wfs') ||
    urlLower.includes('request=getfeature')
  );
}

export type PlaybackWfsLayerSelection = {
  readonly keys: string[];
  readonly exceedsExplicitCapacity: boolean;
};

export type SurveyPlaybackPanelLayer = {
  readonly key: string;
  readonly type: string;
  readonly endpoint: string;
  readonly checked: boolean;
};

export type SurveyPlaybackSelectionDecision =
  | { readonly outcome: 'no-wfs-layers' }
  | {
      readonly outcome: 'capacity-limit';
      readonly selection: PlaybackWfsLayerSelection;
    }
  | {
      readonly outcome: 'ok';
      readonly selection: PlaybackWfsLayerSelection;
    };

/**
 * Playback / drop selection: use checked WFS image layers when any are selected;
 * otherwise play WFS image layers for the survey (capped at maxLayers)
 */
export function resolvePlaybackWfsLayerKeys(
  layers: ReadonlyArray<SurveyPlaybackPanelLayer>,
  maxLayers: number = Number.POSITIVE_INFINITY,
): string[] {
  return resolvePlaybackWfsLayerSelection(layers, maxLayers).keys;
}

export function resolvePlaybackWfsLayerSelection(
  layers: ReadonlyArray<SurveyPlaybackPanelLayer>,
  maxLayers: number = Number.POSITIVE_INFINITY,
): PlaybackWfsLayerSelection {
  const wfsLayers = layers.filter(isWfsImageLayer);
  const checkedKeys = wfsLayers
    .filter((layer) => layer.checked)
    .map((layer) => layer.key);

  if (checkedKeys.length > 0) {
    return {
      keys: checkedKeys,
      exceedsExplicitCapacity:
        Number.isFinite(maxLayers) && checkedKeys.length > maxLayers,
    };
  }

  const allKeys = wfsLayers.map((layer) => layer.key);
  if (Number.isFinite(maxLayers) && allKeys.length > maxLayers) {
    return {
      keys: allKeys.slice(0, maxLayers),
      exceedsExplicitCapacity: false,
    };
  }
  return { keys: allKeys, exceedsExplicitCapacity: false };
}

/**
 * Selection gate shared by Play button and pegman drop
 * Capacity fails only when the user explicitly checked too many WFS layers
 */
export function resolveSurveyPlaybackSelection(
  panelLayers: ReadonlyArray<SurveyPlaybackPanelLayer>,
  maxSimultaneousImages: number,
): SurveyPlaybackSelectionDecision {
  const selection = resolvePlaybackWfsLayerSelection(
    panelLayers,
    maxSimultaneousImages,
  );
  if (selection.keys.length === 0) {
    return { outcome: 'no-wfs-layers' };
  }
  if (selection.exceedsExplicitCapacity) {
    return { outcome: 'capacity-limit', selection };
  }
  return { outcome: 'ok', selection };
}

export function getFeaturePoint(feature: LayerFeature): PointLike | null {
  const geometry = feature.geometry as FeatureGeometryLike | null | undefined;
  if (!geometry) {
    return null;
  }
  if (
    geometry.type === 'point' &&
    typeof geometry.x === 'number' &&
    typeof geometry.y === 'number'
  ) {
    return geometry;
  }
  if (
    geometry.extent?.center &&
    typeof geometry.extent.center.x === 'number' &&
    typeof geometry.extent.center.y === 'number'
  ) {
    return geometry.extent.center;
  }
  return null;
}

export function getDistanceBetweenPoints(
  fromPoint: PointLike,
  toPoint: PointLike,
): number {
  let candidatePoint: PointLike = toPoint;
  const fromWkid = fromPoint.spatialReference?.wkid;
  const toWkid = toPoint.spatialReference?.wkid;
  if (fromWkid && toWkid && fromWkid !== toWkid) {
    const projected = projectGeometry(
      toPoint,
      fromPoint.spatialReference,
    ) as PointLike | null;
    if (projected) {
      candidatePoint = projected;
    }
  }

  try {
    const measured = geometryEngine.distance(
      fromPoint,
      candidatePoint,
      'meters',
    );
    if (typeof measured === 'number' && Number.isFinite(measured)) {
      return Math.abs(measured);
    }
  } catch {
  }

  if (
    typeof fromPoint.x === 'number' &&
    typeof fromPoint.y === 'number' &&
    typeof candidatePoint.x === 'number' &&
    typeof candidatePoint.y === 'number'
  ) {
    return Math.hypot(
      fromPoint.x - candidatePoint.x,
      fromPoint.y - candidatePoint.y,
    );
  }

  return Number.POSITIVE_INFINITY;
}

export function resolveNearestImageIndex(
  layers: readonly PlaybackLayer[],
  dropPoint: PointLike,
): {
  readonly imageIndex: number | null;
  readonly spatialIndex: number | null;
  readonly distanceMeters: number;
  readonly layerKey: string | null;
  readonly point: PointLike | null;
} {
  let nearestImageIndex: number | null = null;
  let nearestSpatialIndex: number | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;
  let nearestLayerKey: string | null = null;
  let nearestPoint: PointLike | null = null;

  for (const layer of layers) {
    for (const feature of layer.features) {
      const imageIndex = normalizeImageIndex(feature.imageIndex);
      if (imageIndex === null) {
        continue;
      }
      const featurePoint = getFeaturePoint(feature);
      if (!featurePoint) {
        continue;
      }

      const distance = getDistanceBetweenPoints(dropPoint, featurePoint);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestImageIndex = imageIndex;
        nearestSpatialIndex = normalizeImageIndex(
          feature.spatialIndex ?? feature.attributes?.spatial_index,
        );
        nearestLayerKey = layer.layerKey;
        nearestPoint = featurePoint;
      }
    }
  }

  return {
    imageIndex: nearestImageIndex,
    spatialIndex: nearestSpatialIndex,
    distanceMeters: nearestDistance,
    layerKey: nearestLayerKey,
    point: nearestPoint,
  };
}

/**
 * Picks a single exact feature point for the playback map pin
 * Prefers the drop-nearest layer, then focus owner, then the first match —
 * never averages multi-camera positions
 */
export function resolvePlaybackPinPoint(
  layers: readonly PlaybackLayer[],
  syncValue: number | null,
  syncMode: SyncMode,
  preferredLayerKeys: readonly (string | null | undefined)[] = [],
): PointLike | null {
  if (syncValue === null || layers.length === 0) {
    return null;
  }

  const preferred = preferredLayerKeys.filter(
    (key): key is string => typeof key === 'string' && key.length > 0,
  );
  const orderedLayers =
    preferred.length === 0
      ? layers
      : [
          ...preferred
            .map((key) => layers.find((layer) => layer.layerKey === key))
            .filter((layer): layer is PlaybackLayer => layer != null),
          ...layers.filter((layer) => !preferred.includes(layer.layerKey)),
        ];

  for (const layer of orderedLayers) {
    const feature = layer.features.find(
      (candidate) => getFeatureSyncValue(candidate, syncMode) === syncValue,
    );
    if (!feature) {
      continue;
    }
    const point = getFeaturePoint(feature);
    if (
      point &&
      typeof point.x === 'number' &&
      typeof point.y === 'number'
    ) {
      return point;
    }
  }

  return null;
}

export function toCssRgbaColor(color: {
  readonly r: number;
  readonly g: number;
  readonly b: number;
  readonly a: number;
}): string {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
}
